import { AsyncPipe, UpperCasePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { AuthenticationService } from '@flexyoo/auth';
import { EmployeeBenefitDocumentType } from '@flexyoo/api/models';
import { EmployeeCartBenefit } from '@flexyoo/employee/models';
import { hasDocument } from '@flexyoo/employee/util';
import { environment } from '@flexyoo/environment';
import { MbpEmployeeBasketBenefit } from '@flexyoo/mbp/components';
import { BenefitStatusEnum } from '@flexyoo/models';
import { FlexyooLetDirective } from '@flexyoo/shared/directives';
import { FileUploadComponent } from '@flexyoo/shared/ui';
import { TranslateModule } from '@ngx-translate/core';
import { PdfViewerModule } from 'ng2-pdf-viewer';
import { BehaviorSubject } from 'rxjs';
import { EmployeeBenefitSignFooterComponent } from '../benefit-sign-footer/benefit-sign-footer.component';

@Component({
  selector: 'app-employee-benefit-agreement-sign-digital',
  templateUrl: 'benefit-agreement-sign-digital.component.html',
  styleUrl: './benefit-agreement-sign-digital.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    FlexyooLetDirective,
    MatIcon,
    AsyncPipe,
    TranslateModule,
    EmployeeBenefitSignFooterComponent,
    MatButtonModule,
    PdfViewerModule,
    FileUploadComponent,
    UpperCasePipe,
  ],
})
export class EmployeeBenefitAgreementSignDigitalComponent implements OnChanges {
  agreementFilePath$ = new BehaviorSubject<string>(null);
  file: File;
  protected readonly acceptedFileFormat = ['.pdf'];

  @Input() public benefit: EmployeeCartBenefit | MbpEmployeeBasketBenefit | null = null;
  @Input() public isQuillSignPossible: boolean;
  @Input() public documentType: 'addendum' | 'declaration' = 'addendum';
  @Input() public hasExistingAddendum = false;

  @Output() public readonly cancelSignAgreement = new EventEmitter<void>();
  @Output() public readonly send = new EventEmitter<File>();
  @Output() public readonly downloadBenefitAddendumTemplate = new EventEmitter<void>();
  @Output() public readonly pendingFileChange = new EventEmitter<boolean>();

  constructor(private readonly auth: AuthenticationService) {}

  protected hasAddendumDocument(): boolean {
    if (!this.benefit) {
      return this.hasExistingAddendum;
    }

    return hasDocument(
      this.benefit?.EmployeeBenefitDocuments,
      EmployeeBenefitDocumentType.Addendum
    );
  }

  protected showManualSteps(): boolean {
    return this.benefit?.BenefitState === BenefitStatusEnum.Selected || !this.hasAddendumDocument();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    const benefitChange = changes['benefit'];
    const hasExistingAddendumChange = changes['hasExistingAddendum'];

    const benefit = benefitChange?.currentValue as EmployeeCartBenefit | null | undefined;
    const hasServerAddendum =
      this.hasExistingAddendum ||
      (!!benefit &&
        hasDocument(benefit.EmployeeBenefitDocuments, EmployeeBenefitDocumentType.Addendum));

    // Only clear once the addendum is actually on file server-side: basket refreshes push a new
    // `benefit` object on every poll, and clearing unconditionally on that would silently drop
    // a locally-selected file the user hasn't uploaded yet.
    if ((benefitChange || hasExistingAddendumChange) && hasServerAddendum) {
      this.file = null;
      this.pendingFileChange.emit(false);
      this.agreementFilePath$.next(null);
    }
  }

  onUserAgreementFileChange(fileToUpload?: File) {
    if (!fileToUpload) {
      return;
    }

    this.file = fileToUpload;
    this.pendingFileChange.emit(true);
    const formData = new FormData();
    formData.append('file', fileToUpload, fileToUpload.name);
    this.setAgreementFilePath(fileToUpload);
  }

  private setAgreementFilePath(file: File) {
    const reader = new FileReader();

    reader.addEventListener(
      'load',
      () => {
        this.agreementFilePath$.next(reader.result as string);
      },
      false
    );

    if (file) {
      reader.readAsDataURL(file);
    }
  }

  protected onRemoveAgreementFile(): void {
    this.file = null;
    this.pendingFileChange.emit(false);
    this.agreementFilePath$.next(null);
  }

  get staticUserAgreementHowTo(): string {
    const user = this.auth.getCurrentUser();
    if (user && user.Language) {
      return `${
        environment.static
      }/instructions/How to sign Agreement ${user.Language.toUpperCase()}.pdf`;
    }

    return '#';
  }

  protected cancelSign(): void {
    this.onRemoveAgreementFile();
    this.cancelSignAgreement.emit();
  }
}
